// ============================================
// CAP.16 D#8 — Monthly Model Performance Audit Report
//
// Renders the stored/generated monthly report at /admin/model-performance.
// Calls getModelPerformanceAuditReport (HTTP GET, Bearer auth).
// Accessible to: owner, builder, medical_director (Dr. Calapai).
//
// c1 — Report generated monthly (Cloud Function scheduled 1st of month 06:00 UTC)
// c2 — Per-model extraction accuracy from human review corrections
// c3 — Disagreement frequency and patterns by biomarker type
// c4 — Accessible to technical operators and Dr. Calapai
// c5 — Report stored in Firestore; served as JSON; displayed here
// ============================================

import { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { CF_URLS } from "../../../config/api";

// ── Types matching Cloud Function response ──────────────────────────────

interface ModelAccuracyEntry {
  modelName: string;
  totalExtractions: number;
  correctedCount: number;
  approvedCount: number;
  rejectedCount: number;
  correctionRatePct: number;
  approvalRatePct: number;
}

interface BiomarkerDisagreementEntry {
  biomarker: string;
  disagreementCount: number;
  avgMaxDeltaPct: number;
  peakMaxDeltaPct: number;
}

interface ModelPerformanceAuditReport {
  month: string;
  generatedAt: string;
  periodStart: string;
  periodEnd: string;
  totalExtractionsScanned: number;
  humanReviewedCount: number;
  modelAccuracy: ModelAccuracyEntry[];
  biomarkerDisagreements: BiomarkerDisagreementEntry[];
  overallAgreementDistribution: { high: number; medium: number; low: number };
  tiebreakerUsedCount: number;
  humanEscalationCount: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function prevMonthStr(): string {
  const now = new Date();
  const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, "0")}`;
}

async function fetchBearerToken(): Promise<string | null> {
  const user = getAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

// ── Styles (design system: DM Sans body, Instrument Serif title, JetBrains Mono data) ──

const s = {
  page: {
    maxWidth: 900,
    margin: "0 auto",
    fontFamily: "'DM Sans', sans-serif",
  } as React.CSSProperties,

  title: {
    fontFamily: "'Instrument Serif', Georgia, serif",
    fontSize: 28,
    fontWeight: 400,
    color: "#18181B",
    margin: "0 0 8px 0",
  } as React.CSSProperties,

  subtitle: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: "#71717A",
    margin: "0 0 20px 0",
  } as React.CSSProperties,

  controls: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 24,
    flexWrap: "wrap" as const,
  } as React.CSSProperties,

  monthInput: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    padding: "6px 10px",
    border: "1px solid #E4E4E7",
    borderRadius: 4,
    color: "#18181B",
    background: "#FFFFFF",
  } as React.CSSProperties,

  btn: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: 4,
    border: "1px solid #E4E4E7",
    background: "#FFFFFF",
    color: "#18181B",
    cursor: "pointer",
    transition: "background 0.15s",
  } as React.CSSProperties,

  btnPrimary: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: 4,
    border: "none",
    background: "#18181B",
    color: "#FFFFFF",
    cursor: "pointer",
    transition: "opacity 0.15s",
  } as React.CSSProperties,

  card: {
    background: "#FFFFFF",
    border: "1px solid #E4E4E7",
    borderRadius: 4,
    padding: 16,
    marginBottom: 20,
  } as React.CSSProperties,

  sectionLabel: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    color: "#18181B",
    marginBottom: 12,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  } as React.CSSProperties,

  mono: {
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    fontSize: 13,
  } as React.CSSProperties,

  row: {
    display: "flex",
    gap: 32,
    flexWrap: "wrap" as const,
  } as React.CSSProperties,

  statItem: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
  } as React.CSSProperties,

  statLabel: {
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    fontSize: 11,
    color: "#71717A",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
  } as React.CSSProperties,

  statValue: {
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    fontSize: 20,
    fontWeight: 700,
    color: "#18181B",
  } as React.CSSProperties,

  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
  } as React.CSSProperties,

  th: {
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    fontSize: 11,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    color: "#71717A",
    padding: "8px 10px",
    textAlign: "left" as const,
    borderBottom: "1px solid #E4E4E7",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,

  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #F4F4F5",
    color: "#18181B",
    verticalAlign: "top" as const,
  } as React.CSSProperties,

  errorBox: {
    background: "#FFF1F2",
    border: "1px solid #E11D48",
    borderRadius: 4,
    padding: 16,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: "#9F1239",
    marginBottom: 20,
  } as React.CSSProperties,

  emptyBox: {
    background: "#F4F4F5",
    border: "1px solid #E4E4E7",
    borderRadius: 4,
    padding: 24,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: "#71717A",
    textAlign: "center" as const,
    fontStyle: "italic",
    marginBottom: 20,
  } as React.CSSProperties,

  barWrap: {
    display: "flex",
    height: 10,
    borderRadius: 4,
    overflow: "hidden",
    background: "#F4F4F5",
    marginTop: 12,
    marginBottom: 8,
  } as React.CSSProperties,
};

// ── Agreement distribution bar ─────────────────────────────────────────

function AgreementBar({ dist, total }: {
  dist: { high: number; medium: number; low: number };
  total: number;
}) {
  if (total === 0) return null;
  const highPct = Math.round((dist.high / total) * 100);
  const medPct = Math.round((dist.medium / total) * 100);
  const lowPct = 100 - highPct - medPct;

  return (
    <div>
      <div style={s.barWrap}>
        {highPct > 0 && (
          <div style={{ width: `${highPct}%`, background: "#16A34A", transition: "width 0.3s" }} />
        )}
        {medPct > 0 && (
          <div style={{ width: `${medPct}%`, background: "#D97706", transition: "width 0.3s" }} />
        )}
        {lowPct > 0 && (
          <div style={{ width: `${lowPct}%`, background: "#E11D48", transition: "width 0.3s" }} />
        )}
      </div>
      <div style={{ display: "flex", gap: 16, fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: "#71717A" }}>
        <span style={{ color: "#16A34A" }}>■ High {highPct}%</span>
        <span style={{ color: "#D97706" }}>■ Medium {medPct}%</span>
        <span style={{ color: "#E11D48" }}>■ Low {lowPct}%</span>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export function ModelPerformanceAuditPage() {
  const [month, setMonth] = useState<string>(prevMonthStr());
  const [report, setReport] = useState<ModelPerformanceAuditReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(async (regenerate = false) => {
    setLoading(true);
    setError(null);
    setReport(null);

    try {
      const token = await fetchBearerToken();
      if (!token) {
        setError("Not authenticated. Please sign in as an admin user.");
        return;
      }

      const url = new URL(CF_URLS.modelPerformanceAuditReport);
      url.searchParams.set("month", month);
      if (regenerate) url.searchParams.set("regenerate", "true");

      const resp = await fetch(url.toString(), {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (resp.status === 401) {
        setError("Authentication failed. Your session may have expired — please refresh.");
        return;
      }
      if (resp.status === 403) {
        setError("Access denied. Only authorized administrators may view model performance reports.");
        return;
      }
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        setError((body as { message?: string }).message ?? `Server error ${resp.status}`);
        return;
      }

      const data = (await resp.json()) as ModelPerformanceAuditReport;
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unexpected error loading report.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  // Auto-load on mount
  useEffect(() => {
    loadReport(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalAgreement = report
    ? report.overallAgreementDistribution.high +
      report.overallAgreementDistribution.medium +
      report.overallAgreementDistribution.low
    : 0;

  return (
    <div style={s.page}>
      <h2 style={s.title}>Model Performance Audit</h2>
      <p style={s.subtitle}>
        Monthly extraction accuracy from provenance data — CAP.16 D#8.
        Generated on the 1st of each month at 06:00 UTC, stored in Firestore.
      </p>

      {/* Month controls */}
      <div style={s.controls}>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={s.monthInput}
          aria-label="Select report month"
        />
        <button
          style={s.btnPrimary}
          onClick={() => loadReport(false)}
          disabled={loading}
          aria-label="Load report"
        >
          {loading ? "Loading…" : "Load Report"}
        </button>
        <button
          style={s.btn}
          onClick={() => loadReport(true)}
          disabled={loading}
          title="Force re-computation from provenance data, then store result"
          aria-label="Regenerate report"
        >
          Regenerate
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div style={s.errorBox} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Empty / no report */}
      {!loading && !error && !report && (
        <div style={s.emptyBox}>
          No report available for {month}. Reports are generated on the 1st of each month
          at 06:00 UTC, or click <strong>Regenerate</strong> to build one on demand.
        </div>
      )}

      {/* Report body */}
      {report && (
        <>
          {/* Summary card — c1, c2, c3 summary numbers */}
          <div style={s.card}>
            <div style={s.sectionLabel}>Summary — {report.month}</div>
            <div style={{ ...s.mono, fontSize: 12, color: "#71717A", marginBottom: 14 }}>
              Period: {new Date(report.periodStart).toLocaleDateString()} →{" "}
              {new Date(report.periodEnd).toLocaleDateString()} &nbsp;·&nbsp;
              Generated: {new Date(report.generatedAt).toLocaleString()}
            </div>
            <div style={s.row}>
              <div style={s.statItem}>
                <span style={s.statLabel}>Extractions Scanned</span>
                <span style={s.statValue}>{report.totalExtractionsScanned.toLocaleString()}</span>
              </div>
              <div style={s.statItem}>
                <span style={s.statLabel}>Human-Reviewed</span>
                <span style={s.statValue}>{report.humanReviewedCount.toLocaleString()}</span>
              </div>
              <div style={s.statItem}>
                <span style={s.statLabel}>Tiebreaker Invoked</span>
                <span style={s.statValue}>{report.tiebreakerUsedCount.toLocaleString()}</span>
              </div>
              <div style={s.statItem}>
                <span style={s.statLabel}>Human Escalations</span>
                <span style={s.statValue}>{report.humanEscalationCount.toLocaleString()}</span>
              </div>
            </div>

            {/* Agreement distribution bar */}
            {totalAgreement > 0 && (
              <AgreementBar
                dist={report.overallAgreementDistribution}
                total={totalAgreement}
              />
            )}
          </div>

          {/* Per-model accuracy table — c2 */}
          <div style={s.card}>
            <div style={s.sectionLabel}>Per-Model Extraction Accuracy</div>
            {report.modelAccuracy.length === 0 ? (
              <p style={{ fontStyle: "italic", color: "#71717A", fontSize: 13 }}>
                No model extraction data for this period.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Model</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Total Extractions</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Approved</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Corrected</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Rejected</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Correction Rate</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Approval Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.modelAccuracy.map((row) => (
                      <tr key={row.modelName}>
                        <td style={{ ...s.td, ...s.mono, fontWeight: 500 }}>{row.modelName}</td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          {row.totalExtractions.toLocaleString()}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", color: "#16A34A" }}>
                          {row.approvedCount.toLocaleString()}
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          {row.correctedCount.toLocaleString()}
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          {row.rejectedCount.toLocaleString()}
                        </td>
                        <td style={{
                          ...s.td,
                          textAlign: "right",
                          color: row.correctionRatePct > 20 ? "#E11D48" : "#18181B",
                          fontWeight: row.correctionRatePct > 20 ? 600 : 400,
                        }}>
                          {row.correctionRatePct.toFixed(1)}%
                        </td>
                        <td style={{
                          ...s.td,
                          textAlign: "right",
                          color: row.approvalRatePct >= 80 ? "#16A34A" : "#18181B",
                          fontWeight: row.approvalRatePct >= 80 ? 600 : 400,
                        }}>
                          {row.approvalRatePct.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#71717A", marginTop: 10, marginBottom: 0 }}>
              Correction rate &gt;20% flagged in{" "}
              <span style={{ color: "#E11D48" }}>rose</span>.{" "}
              Approval rate ≥80% flagged in{" "}
              <span style={{ color: "#16A34A" }}>green</span>.
            </p>
          </div>

          {/* Biomarker disagreement table — c3 */}
          <div style={s.card}>
            <div style={s.sectionLabel}>Biomarker Disagreement Patterns</div>
            {report.biomarkerDisagreements.length === 0 ? (
              <p style={{ fontStyle: "italic", color: "#71717A", fontSize: 13, margin: 0 }}>
                No disagreements recorded for this period.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Biomarker</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Disagreements</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Avg Delta %</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Peak Delta %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.biomarkerDisagreements.map((row) => (
                      <tr key={row.biomarker}>
                        <td style={{ ...s.td, ...s.mono, fontWeight: 500 }}>{row.biomarker}</td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          {row.disagreementCount.toLocaleString()}
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          {row.avgMaxDeltaPct.toFixed(1)}%
                        </td>
                        <td style={{
                          ...s.td,
                          textAlign: "right",
                          color: row.peakMaxDeltaPct > 50 ? "#E11D48" : "#18181B",
                          fontWeight: row.peakMaxDeltaPct > 50 ? 600 : 400,
                        }}>
                          {row.peakMaxDeltaPct.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#71717A", marginTop: 10, marginBottom: 0 }}>
              Peak delta &gt;50% flagged in{" "}
              <span style={{ color: "#E11D48" }}>rose</span>.
              Sorted by disagreement count, descending.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
